import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import {
  Stack,
  Button,
  Text,
  Box as Wrapper,
  Flex,
  Divider,
  Space,
  Icon,
  LinkButton,
} from '@tonic-ui/react';
import { useTranslation, Trans } from 'react-i18next';

import { createWSSession, confirmWSUser } from '../../services/ds-api';
import { useStorageState } from '../../hooks/useStorageState';
import {
  CheckboxInput,
  FormContainer,
  InputField,
  SubmitButton,
  wsLoginValidation,
} from '../../components/forms';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import { AuthContext } from '../../context/AuthContext';
import WSPasswordResetModal from './ws-password-reset-modal';
import WSPasswordUpdateModal from './ws-password-update-modal';
import useQueryParams from '../../utils/query-params';
import { useMarketplaceDetails } from '../../hooks/useMarketplaceDetails';
import { copyToClipboard } from '../../utils/helpers';

const WSLogin = () => {
  const { eventDistributor, hasRequestErrors } = useContext(AuthContext);

  const [formErrors, setFormErrors] = useState({
    error: '',
  });
  const [useMfa, setUseMfa] = useState(false);
  const [uiPref, setUiPref] = useStorageState('localStorage', 'C1C_UI_PREF', {});
  const [copySuccess, setCopySuccess] = useState(false);
  const focusRef = useRef(null);
  const [showWsPasswordResetModal, setShowWSPasswordResetModal] = useState(false);
  const [showWsPasswordUpdateModal, setShowWSPasswordUpdateModal] = useState(false);
  const [showNewSignInBanner] = useState(true);
  const {
    event = '',
    account: passwordUpdateAccount = '',
    username: passwordUpdateUsername = '',
    code: passwordUpdateCode = '',
  } = useQueryParams<AuthQueryParams>(window.location);
  const { t } = useTranslation();
  const { confirmation = '' } = useQueryParams<AuthQueryParams>(window.location);
  const { marketplaceProvider = '', marketplaceToken = '' } = useMarketplaceDetails();
  const [isV1InactiveAccount, setIsV1InactiveAccount] = useStorageState(
    'localStorage',
    'C1C_V1_INACTIVE_ACCOUNT',
    false
  );
  const [showV1InactiveAccountError, setShowV1InactiveAccountError] = useState(false);

  const defaultValues = useMemo(() => {
    if (uiPref?.rememberAccountWS && uiPref?.wsDetails?.account) {
      const { account, username, useMfa } = uiPref.wsDetails;
      return {
        account,
        username,
        password: '',
        mfaCode: '',
        rememberAccountCheck: true,
        useMFACheck: useMfa,
      };
    } else {
      return {
        account: '',
        username: '',
        password: '',
        mfaCode: '',
        rememberAccountCheck: false,
        useMFACheck: false,
      };
    }
  }, [uiPref.rememberAccountWS, uiPref.wsDetails]);

  useEffect(() => {
    if (defaultValues.useMFACheck !== '') setUseMfa(defaultValues.useMFACheck);
  }, [defaultValues.useMFACheck]);

  useEffect(() => {
    if (event === 'WSForgotPassword') {
      setShowWSPasswordUpdateModal(true);
    }
  }, [event]);

  useEffect(() => {
    if (confirmation) {
      confirmWSUser(confirmation);
    }
  }, [confirmation]);

  useEffect(() => {
    if (focusRef.current) {
      focusRef.current = focusRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (isV1InactiveAccount) {
      setIsV1InactiveAccount(false);
      setShowV1InactiveAccountError(true);
    }
  }, [isV1InactiveAccount]);

  const handleWsSubmit = async ({
    account,
    username,
    password,
    mfaCode,
    rememberAccountCheck,
    useMFACheck,
    setSubmitting,
    resetForm,
  }) => {
    setFormErrors({
      ...formErrors,
      error: '',
    });

    // Set rememberAccount preference for WS
    if (rememberAccountCheck) {
      setUiPref({
        ...uiPref,
        rememberAccountWS: true,
        wsDetails: {
          account,
          username,
          useMfa: useMFACheck,
        },
      });
    } else {
      setUiPref({ ...uiPref, rememberAccountWS: false, wsDetails: {} });
    }

    try {
      const { loggedInUser, error, ...rest } = await createWSSession(
        account,
        username,
        password,
        mfaCode
      );
      if (error) {
        console.error(error);

        setFormErrors({
          ...formErrors,
          error: error.includes('SecurityException')
            ? t('wsLogin.credentialsInvalid')
            : error.includes('503')
            ? t('wsLogin.serviceUnavailable')
            : error,
        });
        setSubmitting(false);
        resetForm();
      }
      if (loggedInUser) {
        const { RID } = loggedInUser;
        sessionStorage.setItem('rID', RID);
        localStorage.setItem('C1C_SSO_V2_ENABLED', false);
        eventDistributor.dispatch({ type: 'WS_LOGGED_IN' });
      }
    } catch (error) {
      console.error(error);

      setFormErrors({
        ...formErrors,
        error: error.message ? error.message : error,
      });

      setSubmitting(false);
      resetForm();
    }
  };

  return (
    <Wrapper width={1275} margin="62px auto">
      {marketplaceToken !== '' ? (
        <NotificationBanner
          name="marketplace-info"
          severity="info"
          backgroundColor="yellow:50"
          shouldOpen={true}
          showCloseButton={true}
          width="100%"
          bannerText={`loginView.marketplaceBannerText.${marketplaceProvider}`}
          bannerButtonTxt={
            copySuccess
              ? 'loginView.copyToClipboardSuccessMsg'
              : 'loginView.marketPlaceBannerBtnText'
          }
          bannerButtonIcon={
            copySuccess ? <Icon icon="check-bold" color="#00A94F" marginRight={10}></Icon> : <></>
          }
          clickHandler={async () => setCopySuccess(await copyToClipboard(window.location.href))}
        />
      ) : showNewSignInBanner ? (
        <NotificationBanner
          name="new-signin-warning"
          severity="warning"
          showCloseButton={true}
          shouldOpen={true}
          shouldRememberDismiss={true}
          width="100%"
          bannerLinkTxt="learnMore"
          bannerLink={t('loginView.newSignInBannerLink')}
          bannerText="loginView.newSignInBannerText"
        />
      ) : (
        // Show empty space (same as banner height) for Email Address tab
        <div style={{ height: '59px' }} />
      )}

      {hasRequestErrors?.error && (
        <NotificationBanner
          name="error-info"
          severity="error"
          shouldOpen={true}
          showCloseButton={true}
          width="100%"
          bannerText={`errorMessaging.title`}
        />
      )}

      {showWsPasswordResetModal && (
        <WSPasswordResetModal
          isOpen={showWsPasswordResetModal}
          onClose={() => setShowWSPasswordResetModal(false)}
        />
      )}
      {showWsPasswordUpdateModal && (
        <WSPasswordUpdateModal
          account={passwordUpdateAccount}
          username={passwordUpdateUsername}
          code={passwordUpdateCode}
          isOpen={showWsPasswordUpdateModal}
          onClose={() => setShowWSPasswordUpdateModal(false)}
        />
      )}
      <Wrapper width={360} margin="43px auto">
        <Stack direction="column" spacing="4x">
          <Text size="2xl" lineHeight="xl">
            {t('signIn')}
          </Text>
          <Wrapper>
            {formErrors?.error && (
              <NotificationBanner
                name="ws-login-error"
                severity="error"
                shouldOpen={true}
                bannerText={formErrors.error}
              />
            )}
            {showV1InactiveAccountError && (
              <NotificationBanner
                name="v1-inactive-account-error"
                severity="error"
                shouldOpen={true}
                bannerText="notActiveAccountError.message"
                bannerLinkTxt="notActiveAccountError.linkText"
                bannerLink={t('notActiveAccountError.supportLink')}
              />
            )}

            <FormContainer
              id="ws-login-form"
              customValidation={wsLoginValidation}
              initialValues={defaultValues}
              enableReinitialize={true}
              submitAction={(formPayload) => handleWsSubmit(formPayload)}
            >
              <Stack direction="column" spacing="4x" mt="32px">
                <InputField
                  id="ws-account"
                  type="text"
                  label={t('wsCommon.account')}
                  name="account"
                  isRequired={true}
                  ref={focusRef}
                />
                <Space />
                <InputField
                  id="ws-username"
                  type="text"
                  label={t('wsCommon.username')}
                  name="username"
                  isRequired={true}
                />
                <Space />
                <InputField
                  id="ws-password"
                  type="password"
                  label={t('password')}
                  name="password"
                  isRequired={true}
                />

                <LinkButton
                  marginTop={5}
                  onClick={() => {
                    setShowWSPasswordResetModal(true);
                  }}
                >
                  {t('forgotPassword')}
                </LinkButton>

                <CheckboxInput
                  label={t('wsLogin.rememberCheckboxLabel')}
                  name="rememberAccountCheck"
                />
                <Space />
                <CheckboxInput
                  label={t('wsLogin.useMFA')}
                  name="useMFACheck"
                  onCheckboxChange={(value) => setUseMfa(value)}
                />
                {useMfa && (
                  <>
                    <Space height="4x" />
                    <InputField
                      id="ws-mfa-code"
                      type="text"
                      label={t('wsLogin.mfaCode')}
                      name="mfaCode"
                      isRequired={true}
                    />
                  </>
                )}
                <Space />
                <SubmitButton label={t('signIn')} id="v1-login-submit-btn" />
              </Stack>
            </FormContainer>
          </Wrapper>

          <Flex>
            <Divider height={20} flex="auto" />
            <Text flex="initial" height={20} margin="8px">
              {t('or')}
            </Text>
            <Divider height={20} flex="auto" />
          </Flex>
          <Button
            onClick={() => eventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: 'trial' })}
          >
            {t('signUp')}
          </Button>
          <Text alignSelf="center">
            <Trans i18nKey="loginView.signUpInfo" />
          </Text>
        </Stack>
      </Wrapper>
    </Wrapper>
  );
};

export default WSLogin;
